import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, User, Building2, Phone, Mail, MapPin, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useGlobalAdmin } from '@/hooks/useGlobalAdmin';

interface LoanOfficer {
  id: string;
  first_name: string;
  last_name: string;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  created_at: string;
  coverage_count?: number;
}

export default function LoanOfficersManage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isGlobalAdmin, loading: adminLoading, checked: adminChecked } = useGlobalAdmin();
  const { toast } = useToast();
  const [loanOfficers, setLoanOfficers] = useState<LoanOfficer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/admin/login');
    }
  }, [user, authLoading, navigate]);

  // Redirect non-global admins - only check after admin status has been definitively checked
  useEffect(() => {
    // Wait for the admin check to complete (not just loading, but actually checked)
    if (!adminChecked) return;
    
    // Only redirect if we have a user but they're definitively not a global admin
    if (user && !isGlobalAdmin) {
      toast({ title: 'Access Denied', description: 'Only global admins can manage loan officers', variant: 'destructive' });
      navigate('/admin');
    }
  }, [user, adminChecked, isGlobalAdmin, navigate, toast]);

  useEffect(() => {
    if (isGlobalAdmin) {
      fetchLoanOfficers();
    }
  }, [isGlobalAdmin]);

  const fetchLoanOfficers = async () => {
    setLoading(true);
    
    // Fetch all loan officers (no site filter)
    const { data: officers, error } = await supabase
      .from('loan_officers')
      .select('id, first_name, last_name, company_name, email, phone, is_active, created_at')
      .order('last_name', { ascending: true });

    if (error) {
      toast({ title: 'Error', description: 'Failed to load loan officers', variant: 'destructive' });
      setLoading(false);
      return;
    }

    // Fetch coverage counts
    const { data: coverage } = await supabase
      .from('loan_officer_coverage')
      .select('loan_officer_id');

    const coverageCounts: Record<string, number> = {};
    coverage?.forEach(c => {
      coverageCounts[c.loan_officer_id] = (coverageCounts[c.loan_officer_id] || 0) + 1;
    });

    const officersWithCounts = (officers || []).map(o => ({
      ...o,
      coverage_count: coverageCounts[o.id] || 0,
    }));

    setLoanOfficers(officersWithCounts);
    setLoading(false);
  };


  if (authLoading || !adminChecked || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isGlobalAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-background border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/admin" className="text-muted-foreground hover:text-foreground">
              ← Back to Dashboard
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Loan Officers</h1>
          <p className="text-muted-foreground mt-1">
            Manage mortgage loan officers across all sites. Officers appear on any site where they have coverage.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Loan Officers</CardTitle>
            <CardDescription>
              Loan officers are shared across all sites based on their town/area coverage selections.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loanOfficers.length === 0 ? (
              <div className="text-center py-12">
                <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Loan Officers Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Add your first mortgage loan officer to get started.
                </p>
                <Button onClick={() => navigate('/admin/loan-officers/new')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Loan Officer
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Coverage</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loanOfficers.map((officer) => (
                    <TableRow key={officer.id}>
                      <TableCell>
                        <Link 
                          to={`/admin/loan-officers/${officer.id}/edit`}
                          className="font-medium hover:underline text-primary"
                        >
                          {officer.first_name} {officer.last_name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {officer.company_name ? (
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                            {officer.company_name}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {officer.email && (
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="h-3 w-3 text-muted-foreground" />
                              {officer.email}
                            </div>
                          )}
                          {officer.phone && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-3 w-3 text-muted-foreground" />
                              {officer.phone}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>{officer.coverage_count} area{officer.coverage_count !== 1 ? 's' : ''}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={officer.is_active ? 'default' : 'secondary'}>
                          {officer.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/loan-officers/${officer.id}/edit`)}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
